import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionItem, Button, Input, Textarea, Tabs, Tab, addToast, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Tooltip } from "@heroui/react";
import { X, Plus, Menu, ChevronDown, ChevronRight, Trash2, ShoppingCart } from "lucide-react";
import AppButton from "../common/AppButton";
import axios from "axios";
import {
  clearSelectedItemsForCurrentUser,
  ensureSelectedItemsStoreForCurrentUser,
  loadSelectedItemsForCurrentUser,
  saveSelectedItemsForCurrentUser,
} from "../../services/userScopedStorage";

// Custom Accordion Item Component for v2 compatibility
const CustomAccordionItem = ({ 
  para, 
  speakerLabel, 
  timeDisplay, 
  timeRange, 
  selectedSession,
  onRemove
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="mb-3 rounded-xl border border-white/10 bg-surfaceContainer first:mt-4">
      {/* Custom Header with Select as sibling */}
      <div className="flex items-center justify-between w-full px-4 py-3 border-b border-white/10">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 min-w-0 text-left hover:bg-surfaceContainer/50 rounded transition-colors"
          aria-expanded={isExpanded}
        >
          <span className="text-base font-medium text-onSurface truncate">{speakerLabel} - {timeDisplay}</span>
          <span className="text-onSurface flex-shrink-0">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </button>
        <Button
          isIconOnly
          size="sm"
          className="ml-2 bg-surface text-onSurface hover:bg-danger/20"
          onPress={() => onRemove?.(para?.id)}
          aria-label="Remove entry"
        >
          <Trash2 size={14} />
        </Button>
      </div>
      
      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-4 pb-3 pt-3">
          <div className="p-4 text-sm text-onSurface/80 bg-surface rounded-lg">
            <div className="font-semibold text-blue-300 mb-2">
              {timeDisplay}{timeRange}
            </div>
            <div className="whitespace-pre-wrap text-onSurface leading-relaxed">
              {para.corrected_content || para.original_content || para.raw_content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Global store for selected items per session (using window object)
if (!window.selectedItemsStore) {
  ensureSelectedItemsStoreForCurrentUser();
}

// Global store for speaker-to-section assignments
if (!window.speakerSectionAssignmentsStore) {
  window.speakerSectionAssignmentsStore = {};
}

// Global store for session data (speakers and paragraphs)
if (!window.sessionDataStore) {
  window.sessionDataStore = new Map();
}

// Global store for sections data
if (!window.sectionsStore) {
  window.sectionsStore = {
    sections: [],
    nextSectionId: 1
  };
}

const SitzungViewConfig = ({
  localNavData = [],
  speakers = [],
  paragraphChildren = [],
  sessionId,
  openTabs = [],
  hideHeader = false,
  initialView = "sessions",
  loadingSelectedSessionIds = new Set(),
  isLoading = false,
}) => {
  // Load initial assignments from global store (structure: { sessionId: { speakerKey: assignedSessionKey } })
  const [speakerSectionAssignments, setSpeakerSectionAssignments] = useState(() => {
    return window.speakerSectionAssignmentsStore || {};
  });
  const [selectedSession, setSelectedSession] = useState(null);
  const [storeVersion, setStoreVersion] = useState(0); // Used to force re-render when store updates
  const [activeView, setActiveView] = useState(initialView); // "sessions" | "topics"
  const [sections, setSections] = useState(() => {
    // Load sections from global store on mount; ensure a single default section
    const existing = window.sectionsStore?.sections || [];
    if (existing.length > 0) return existing.slice(0, 1);
    const defaultSection = {
      id: window.sectionsStore?.nextSectionId || 1,
      name: "sec 1",
      title: "",
      description: "",
      entries: []
    };
    return [defaultSection];
  });
  const [selectedSection, setSelectedSection] = useState(null);
  const [nextSectionId, setNextSectionId] = useState(() => {
    // Load nextSectionId from global store on mount
    return (window.sectionsStore?.nextSectionId || 1) + 1;
  });
  const navigate = useNavigate();
  const [summaryName, setSummaryName] = useState("");
  const [articleSummaryName, setArticleSummaryName] = useState("");
  const [isSummarizingThema, setIsSummarizingThema] = useState(false);
  const [isSummarizingArtikel, setIsSummarizingArtikel] = useState(false);
  // Both summarization paths are locked to Agentic + Fließtext — the
  // Standard chunk-merge legacy path and the Stichpunkte / Beide bullet
  // variants have been retired. Constants (not state) so no toggle UI
  // can flip them at runtime.
  const bulletSummary = false;         // Fließtext
  const bulletSummaryArtikel = false;  // Fließtext
  // Custom prompt state — per (type, mode) combination, not persisted to DB
  // Keys: "thema-fliesstext", "thema-stichpunkte", "artikel-fliesstext", "artikel-stichpunkte"
  // Free-text guidance the user types per generation. Threaded to the backend
  // as `custom_prompt` and prepended to the drafter system prompt as
  // top-priority guidance (see `with_user_priority` in prompts.py). Empty
  // string = no guidance sent; the drafter uses its standard system prompt.
  const [userGuidanceThema, setUserGuidanceThema] = useState("");
  const [userGuidanceArtikel, setUserGuidanceArtikel] = useState("");
  const [showGuidanceThema, setShowGuidanceThema] = useState(false);
  const [showGuidanceArtikel, setShowGuidanceArtikel] = useState(false);
  const [pendingDeleteSession, setPendingDeleteSession] = useState(null);
  const {
    isOpen: isResetOpen,
    onOpen: openReset,
    onOpenChange: onResetChange,
    onClose: closeReset,
  } = useDisclosure();
  const {
    isOpen: isDeleteSessionOpen,
    onOpen: openDeleteSession,
    onOpenChange: onDeleteSessionChange,
  } = useDisclosure();

  // Which modes need drafting for the current bullet selection. Since
  // bulletSummary/bulletSummaryArtikel are hard-coded to false right now this
  // always resolves to [false], but the loop is kept so the "beide" option can
  // be re-enabled without touching the send logic.
  const modesForBulletSetting = (bulletSetting) => {
    if (bulletSetting === "beide") return [false, true];
    return [bulletSetting];
  };

  const resolveSessionId = () => {
    if (selectedSession?.sessionId) return selectedSession.sessionId;
    for (const section of sections || []) {
      for (const entry of section.entries || []) {
        if (entry?.sessionId) return entry.sessionId;
      }
    }
    const firstTab = (openTabs || []).find((t) => t.sessionId);
    return firstTab?.sessionId || null;
  };

  const handleSummarizeSections = async () => {
    const sessionId = resolveSessionId();
    const payloadSections = buildSectionsPayload(summaryName?.trim()).filter(
      (section) =>
        Array.isArray(section.entries) &&
        section.entries.some((e) => Array.isArray(e.paragraphs) && e.paragraphs.length > 0)
    );
    if (payloadSections.length === 0) {
      addToast({ title: "Keine Sektionen mit Inhalt", color: "danger" });
      return;
    }

    try {
      setIsSummarizingThema(true);
      const bullets = bulletSummary === "beide" ? [false, true] : [bulletSummary];
      await Promise.all(
        bullets.map((bullet) => {
          const modeSuffix = bullet ? "Stichpunkte" : "Fließtext";
          const name = bulletSummary === "beide"
            ? `${summaryName?.trim()} - ${modeSuffix}`
            : summaryName?.trim() || null;
          const payload = {
            session_id: sessionId ? Number(sessionId) : null,
            name,
            sections: payloadSections,
            bullet_summary: bullet,
            agentic: true,
          };
          const trimmedGuidance = userGuidanceThema.trim();
          if (trimmedGuidance) {
            payload.custom_prompt = trimmedGuidance;
          }
          return axios.post(`transcriptions_poc_000113_1_0_0/summarize-sections`, payload);
        })
      );
      addToast({ title: "Zusammenfassung gestartet", color: "success" });
      navigate("/section-summaries");
    } catch (err) {
      console.error("summarize-sections failed:", err);
      addToast({ title: "Zusammenfassung fehlgeschlagen", color: "danger" });
    } finally {
      setIsSummarizingThema(false);
    }
  };

  const buildSectionsPayload = (overrideTitle) => {
    const base = sections && sections.length > 0 ? sections[0] : null;
    const title = overrideTitle || base?.title || base?.name || "Thema";
    const description = base?.description || "";

    // Build entries from all selected items across sessions
    const entries = [];
    if (window.selectedItemsStore && window.sessionDataStore) {
      const sessionLabelById = new Map(
        (openTabs || []).map((t) => [String(t.sessionId), t.label])
      );
      window.selectedItemsStore.forEach((ids, sessionId) => {
        const selectedIds = Array.isArray(ids) ? ids : [];
        if (!selectedIds.length) return;
        const sessionData = window.sessionDataStore.get(String(sessionId));
        if (!sessionData || !Array.isArray(sessionData.paragraphChildren)) return;

        const idSet = new Set(selectedIds);
        const paragraphs = sessionData.paragraphChildren.filter((p) =>
          idSet.has(p.id)
        );
        if (!paragraphs.length) return;

        const bySpeaker = new Map();
        paragraphs.forEach((p) => {
          const speakerKey = `${p.speaker_name ?? "no"}::${p.speaker_number ?? ""}`;
          if (!bySpeaker.has(speakerKey)) {
            const label =
              p.speaker_name === "no"
                ? `Sprecher ${p.speaker_number ?? ""}`.trim()
                : p.speaker_name || "Sprecher";
            bySpeaker.set(speakerKey, {
              speakerKey,
              speaker: label,
              sitzung: sessionLabelById.get(String(sessionId)) || `Session ${sessionId}`,
              sessionId: String(sessionId),
              time: "",
              paragraphs: [],
            });
          }
          bySpeaker.get(speakerKey).paragraphs.push(p);
        });
        entries.push(...bySpeaker.values());
      });
    }

    return [
      {
        title,
        description,
        entries,
      },
    ];
  };

  // Multi-session selections payload for the agentic backend path. Reads the
  // global selectedItemsStore (sessionId -> paragraph_ids[]) and shapes it as
  // [{session_id, paragraph_ids}], skipping empty sessions. The agentic
  // backend uses the actual DB paragraph ids directly.
  const buildSelectionsPayload = () => {
    const selections = [];
    if (window.selectedItemsStore) {
      window.selectedItemsStore.forEach((ids, sessionId) => {
        const arr = Array.isArray(ids) ? ids.filter((x) => x != null) : [];
        if (!arr.length) return;
        const sid = parseInt(sessionId, 10);
        if (!Number.isFinite(sid)) return;
        selections.push({ session_id: sid, paragraph_ids: arr });
      });
    }
    return selections;
  };

  const handleSummarizeArticles = async () => {
    const payloadSelections = buildSelectionsPayload();
    const totalSelected = payloadSelections.reduce(
      (n, s) => n + (s.paragraph_ids?.length || 0),
      0
    );
    if (totalSelected === 0) {
      addToast({ title: "Keine Absätze ausgewählt", color: "danger" });
      return;
    }

    try {
      setIsSummarizingArtikel(true);
      const bullets = bulletSummaryArtikel === "beide" ? [false, true] : [bulletSummaryArtikel];
      await Promise.all(
        bullets.map((bullet) => {
          const modeSuffix = bullet ? "Stichpunkte" : "Fließtext";
          const name = bulletSummaryArtikel === "beide"
            ? `${articleSummaryName?.trim()} - ${modeSuffix}`
            : articleSummaryName?.trim() || null;

          const payload = {
            name,
            selections: payloadSelections,
            bullet_summary: bullet,
            agentic: true,
          };

          const trimmedGuidance = userGuidanceArtikel.trim();
          if (trimmedGuidance) {
            payload.custom_prompt = trimmedGuidance;
          }
          return axios.post(`transcriptions_poc_000113_1_0_0/summarize-articles`, payload);
        })
      );
      addToast({
        title: `Zusammenfassung gestartet (${totalSelected} Absätze)`,
        color: "success",
      });
      navigate("/article-summaries");
    } catch (err) {
      console.error("summarize-articles failed:", err);
      addToast({ title: "Artikel-Zusammenfassung fehlgeschlagen", color: "danger" });
    } finally {
      setIsSummarizingArtikel(false);
    }
  };

  const handleResetAllSelections = () => {
    try {
      ensureSelectedItemsStoreForCurrentUser();
      window.selectedItemsStore = new Map();
      clearSelectedItemsForCurrentUser();
      window.dispatchEvent(new CustomEvent("selected-items-store-updated"));
      addToast({ title: "Auswahl zurückgesetzt", color: "success" });
    } catch (e) {
      console.warn("[SitzungViewConfig] Failed to reset selections:", e);
      addToast({ title: "Zurücksetzen fehlgeschlagen", color: "danger" });
    }
  };
  
  // Persist sections to global store whenever they change
  useEffect(() => {
    // Enforce single-section invariant when persisting
    const persisted = Array.isArray(sections) && sections.length > 0 ? [sections[0]] : [];
    window.sectionsStore = {
      sections: persisted,
      nextSectionId
    };
  }, [sections, nextSectionId]);
  
  // Update selectedSection when sections change
  useEffect(() => {
    if (sections.length > 0 && (!selectedSection || !sections.find(s => s.id === selectedSection.id))) {
      setSelectedSection(sections[0]);
    } else if (sections.length === 0) {
      // Ensure a default section always exists
      const fallback = {
        id: nextSectionId,
        name: `sec ${nextSectionId}`,
        title: "",
        description: "",
        entries: []
      };
      setSections([fallback]);
      setSelectedSection(fallback);
      setNextSectionId((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);
  
  // Restore selectedSection from store when component mounts
  useEffect(() => {
    if (sections.length > 0 && !selectedSection) {
      setSelectedSection(sections[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store the current session's data in the global store (from props - this is the Settings tab data)
  useEffect(() => {
    if (sessionId && speakers && paragraphChildren) {
      window.sessionDataStore.set(String(sessionId), {
        speakers,
        paragraphChildren
      });
    }
  }, [sessionId, speakers, paragraphChildren]);

  // Get assignments for the currently selected session
  const currentSessionAssignments = selectedSession && selectedSession.sessionId 
    ? speakerSectionAssignments[String(selectedSession.sessionId)] || {}
    : {};

  // Get data from the global store for the selected session
  const getSessionData = (sessionId) => {
    if (!sessionId) return { speakers: [], paragraphChildren: [] };
    const stored = window.sessionDataStore.get(String(sessionId));
    return stored || { speakers: speakers || [], paragraphChildren: paragraphChildren || [] };
  };

  const selectedSessionData = selectedSession?.sessionId ? getSessionData(selectedSession.sessionId) : { speakers, paragraphChildren };
  const selectedSessionSpeakers = selectedSessionData.speakers;
  const selectedSessionParagraphs = selectedSessionData.paragraphChildren;
  const isSelectedSessionLoading =
    !!selectedSession?.sessionId &&
    (loadingSelectedSessionIds?.has?.(String(selectedSession.sessionId)) || isLoading);

  const removeSelectedParagraph = React.useCallback((paragraphId) => {
    if (!selectedSession?.sessionId || paragraphId == null) return;
    const sessionIdStr = String(selectedSession.sessionId);
    ensureSelectedItemsStoreForCurrentUser();
    const current = window.selectedItemsStore.get(sessionIdStr) || [];
    const next = current.filter((id) => id !== paragraphId);
    window.selectedItemsStore.set(sessionIdStr, next);
    try {
      const parsed = loadSelectedItemsForCurrentUser();
      parsed[sessionIdStr] = next;
      saveSelectedItemsForCurrentUser(parsed);
    } catch (e) {
      console.warn("[SitzungViewConfig] Failed to persist selectedItemsStore:", e);
    }
    window.dispatchEvent(new CustomEvent("selected-items-store-updated"));
  }, [selectedSession]);

  const clearSessionSelections = React.useCallback((sessionId) => {
    if (!sessionId) return;
    const sessionIdStr = String(sessionId);
    ensureSelectedItemsStoreForCurrentUser();
    window.selectedItemsStore.set(sessionIdStr, []);
    try {
      const parsed = loadSelectedItemsForCurrentUser();
      parsed[sessionIdStr] = [];
      saveSelectedItemsForCurrentUser(parsed);
    } catch (e) {
      console.warn("[SitzungViewConfig] Failed to persist selectedItemsStore:", e);
    }
    window.dispatchEvent(new CustomEvent("selected-items-store-updated"));
  }, []);

  const selectedParagraphIds = React.useMemo(() => {
    const sessionId = selectedSession?.sessionId;
    if (!sessionId || !window.selectedItemsStore) return [];
    const items = window.selectedItemsStore.get(String(sessionId));
    return Array.isArray(items) ? items : [];
  }, [selectedSession, storeVersion]);

  const selectedParagraphsWithSpeakers = React.useMemo(() => {
    if (!selectedSession) return [];
    const selectedSet = new Set(selectedParagraphIds);
    return selectedSessionParagraphs
      .filter((p) => selectedSet.has(p.id))
      .map((para) => {
        const speaker = selectedSessionSpeakers.find((s, idx) => {
          const speakerKey = s.key || s.label || `speaker-${idx}`;
          return s.speaker_name === para.speaker_name ||
                 s.speaker_number === para.speaker_number ||
                 speakerKey === para.speaker_name;
        });
        return {
          paragraph: para,
          speaker: speaker,
          speakerKey: speaker ? (speaker.key || speaker.label || para.speaker_name) : para.speaker_name,
          speakerLabel: speaker ? (speaker.label || speaker.key || para.speaker_name) : para.speaker_name
        };
      });
  }, [selectedSession, selectedParagraphIds, selectedSessionParagraphs, selectedSessionSpeakers]);

  const areEntriesEqual = React.useCallback((prevEntries = [], nextEntries = []) => {
    if (prevEntries === nextEntries) return true;
    if (!Array.isArray(prevEntries) || !Array.isArray(nextEntries)) return false;
    if (prevEntries.length !== nextEntries.length) return false;
    for (let i = 0; i < prevEntries.length; i += 1) {
      const prev = prevEntries[i] || {};
      const next = nextEntries[i] || {};
      if (prev.speakerKey !== next.speakerKey) return false;
      if (String(prev.sessionId ?? "") !== String(next.sessionId ?? "")) return false;
      const prevParagraphs = Array.isArray(prev.paragraphs) ? prev.paragraphs : [];
      const nextParagraphs = Array.isArray(next.paragraphs) ? next.paragraphs : [];
      if (prevParagraphs.length !== nextParagraphs.length) return false;
      for (let j = 0; j < prevParagraphs.length; j += 1) {
        if (prevParagraphs[j]?.id !== nextParagraphs[j]?.id) return false;
      }
    }
    return true;
  }, []);

  // Auto-assign all selected paragraphs into the single section
  useEffect(() => {
    if (!selectedSession) return;
    setSections((prevSections) => {
      if (!Array.isArray(prevSections) || prevSections.length === 0) return prevSections;
      const baseSection = prevSections[0];
      const grouped = new Map();
      selectedParagraphsWithSpeakers.forEach(({ paragraph, speakerKey, speakerLabel }) => {
        const key = `${speakerKey || "unknown"}::${selectedSession.sessionId}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            speakerKey,
            speaker: speakerLabel,
            sitzung: selectedSession.label || "Unbekannt",
            sessionId: selectedSession.sessionId,
            time: "",
            paragraphs: [],
          });
        }
        grouped.get(key).paragraphs.push(paragraph);
      });

      const nextEntries = Array.from(grouped.values());
      const currentEntries = Array.isArray(baseSection?.entries) ? baseSection.entries : [];
      if (areEntriesEqual(currentEntries, nextEntries)) return prevSections;

      const nextSection = {
        ...baseSection,
        entries: nextEntries,
      };
      setSelectedSection((current) => {
        if (!current) return nextSection;
        if (current.id === nextSection.id) return nextSection;
        return current;
      });
      return [nextSection];
    });
  }, [selectedParagraphsWithSpeakers, selectedSession, areEntriesEqual]);


  // Persist speaker assignments to global store whenever they change
  useEffect(() => {
    // Only update if there's actually a change
    const currentStore = window.speakerSectionAssignmentsStore || {};
    const storeJson = JSON.stringify(currentStore);
    const stateJson = JSON.stringify(speakerSectionAssignments);
    
    if (storeJson !== stateJson) {
      window.speakerSectionAssignmentsStore = speakerSectionAssignments;
      // Dispatch event so other components can react if needed
      window.dispatchEvent(new CustomEvent('speaker-assignments-updated', {
        detail: { assignments: speakerSectionAssignments }
      }));
    }
  }, [speakerSectionAssignments]);

  // Listen for store updates to re-render when selections change
  useEffect(() => {
    const handleStoreUpdate = () => {
      setStoreVersion(prev => prev + 1);
    };

    const handleAssignmentsUpdate = (event) => {
      // If assignments were updated from another instance, sync them
      if (event.detail?.assignments) {
        setSpeakerSectionAssignments(event.detail.assignments);
      }
    };

    window.addEventListener('selected-items-store-updated', handleStoreUpdate);
    window.addEventListener('speaker-assignments-updated', handleAssignmentsUpdate);

    return () => {
      window.removeEventListener('selected-items-store-updated', handleStoreUpdate);
      window.removeEventListener('speaker-assignments-updated', handleAssignmentsUpdate);
    };
  }, []); // Remove dependency to avoid infinite loops

  // Sync with global store when component mounts or selected session changes
  useEffect(() => {
    // Always sync with the latest from global store
    if (window.speakerSectionAssignmentsStore) {
      const currentStore = window.speakerSectionAssignmentsStore;
      const storeJson = JSON.stringify(currentStore);
      const stateJson = JSON.stringify(speakerSectionAssignments);
      
      if (storeJson !== stateJson) {
        setSpeakerSectionAssignments(currentStore);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession]); // Re-sync when session changes (intentionally not including speakerSectionAssignments to avoid loops)

  return (
    <>
    <div className="h-full w-full bg-surfaceContainer flex flex-col">
      {/* Header */}
      {!hideHeader && (
        <div className="border-b border-white/10 p-4 bg-surfaceContainer flex items-center gap-3">
          <AppButton
            onClick={() => window.dispatchEvent(new Event("open-drawer"))}
            width="w-10"
            roundedFull="rounded-full"
            className="p-2"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </AppButton>
          <h2 className="text-xl font-semibold text-onSurface">
          {activeView === "topics" ? "Themenansicht" : "Sitzung View"}
          </h2>
        </div>
      )}
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Sections */}
        <div className="w-72 border-r border-white/10 bg-surfaceContainer/50 p-6 pt-0 flex flex-col">
          <Button
            variant="flat"
            className={`w-full mb-3 justify-start rounded-xl transition-colors ${
              activeView === "topics"
                ? "bg-secondaryContainer hover:bg-secondary/80"
                : "bg-surface hover:bg-surfaceVariant"
            }`}
            size="md"
            onPress={() => {
              setActiveView("topics");
              setSelectedSession(null);
            }}
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="font-medium">Zusammenfassungen</span>
          </Button>
        <div className="flex-1 overflow-y-auto pt-2">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="text-xs font-semibold text-onSurface/70 uppercase tracking-wide mb-2">
              Sitzungen
            </div>
            <div className="flex flex-col gap-3">
              {openTabs && openTabs.length > 0 ? (
                openTabs.map((tab) => {
                  const isSelected = selectedSession?.id === tab.id && activeView === "sessions";
                  const selectedItems = tab.sessionId ? window.selectedItemsStore.get(String(tab.sessionId)) || [] : [];
                  
                  return (
                    <div key={`row-${tab.id}`} className="flex items-center gap-2 w-full">
                      <div className="relative flex-1 min-w-0">
                        <Tooltip content={tab.label || `Tab ${tab.id}`} placement="right" showArrow>
                          <Button
                            key={`tab-${tab.id}`}
                            variant="flat"
                            className={`w-full min-w-0 justify-start rounded-xl text-left transition-colors ${
                              isSelected 
                                ? "bg-secondaryContainer hover:bg-secondary/80" 
                                : "bg-gray-200 hover:bg-gray-300"
                            }`}
                            size="md"
                            onPress={() => {
                              setSelectedSession(tab);
                              setActiveView("sessions");
                            }}
                          >
                            <span className="block truncate">{tab.label || `Tab ${tab.id}`}</span>
                          </Button>
                        </Tooltip>
                        {selectedItems.length > 0 && (
                          <span className="absolute -top-2 -right-2 z-10 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white">
                            {selectedItems.length}
                          </span>
                        )}
                      </div>
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        className="text-danger"
                        isDisabled={!tab.sessionId}
                        aria-label={`Auswahl für ${tab.label || `Tab ${tab.id}`} löschen`}
                        onPress={() => {
                          if (!tab.sessionId) return;
                          setPendingDeleteSession({
                            sessionId: tab.sessionId,
                            label: tab.label || `Tab ${tab.id}`,
                          });
                          openDeleteSession();
                        }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-onSurface/60 text-center py-4">
                  <div>Keine geöffneten Tabs</div>
                </div>
              )}
            </div>
            <div className="mt-4">
              <Button
                size="sm"
                color="danger"
                // variant="light"
                className="w-full rounded-full"
                onPress={openReset}
              >
                Alle Sitzungen entfernen
              </Button>
            </div>
          </div>
        </div>
        </div>

        {/* Right Panel - Speaker Entries or Sections View */}
        <div className="flex-1 flex flex-col overflow-hidden bg-surface mr-6 min-h-0 h-full rounded-lg">
        {activeView === "topics" ? (
          /* Themen + Artikel View */
          <div className="flex-1 flex flex-col overflow-y-auto p-6 gap-6">
            {/* Single Section Header */}
            <div className=" flex items-center gap-2 p-2 rounded-lg">
              <div className="text-sm font-semibold text-onSurface">
                Thema
              </div>
            </div>

            {/* Main content area */}
            {selectedSection ? (
              <>
              <div className="flex flex-col bg-white rounded-lg p-6 shadow-sm">
                {/* Summary name */}
                <div className="mb-6">
                  <Input
                    label="Zusammenfassungstitel"
                    placeholder="z.B. Vorstand Q1"
                    value={summaryName}
                    onChange={(e) => setSummaryName(e.target.value)}
                    className="w-full"
                  />
                </div>

                {/* Description textarea */}
                <div className="mb-6">
                  <Textarea
                    label="Kontext für die AI"
                    placeholder="Kontext für die AI eingeben"
                    value={selectedSection.description}
                    onChange={(e) => {
                      setSections(sections.map(s => 
                        s.id === selectedSection.id 
                          ? { ...s, description: e.target.value }
                          : s
                      ));
                      setSelectedSection({ ...selectedSection, description: e.target.value });
                    }}
                    className="w-full"
                    minRows={3}
                  />
                </div>

                {/* Entries section intentionally hidden in Themen view */}

                <div className="text-sm text-onSurface/70 leading-relaxed">
                  Erstellt eine zusammengeführte Themen-Zusammenfassung aus den ausgewählten Absätzen
                  über mehrere Sitzungen.<br />Die Ausgabe wird als eine einzige Zusammenfassung generiert.
                </div>

                {/* Free-text user guidance — prepended to the drafter system
                    prompt with top priority (see with_user_priority in
                    prompts.py). Empty = no guidance sent. */}
                <div className="mt-4 border-t border-gray-200 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowGuidanceThema((v) => !v)}
                    className="text-xs text-onSurface/70 hover:text-onSurface flex items-center gap-1"
                  >
                    {showGuidanceThema ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Zusätzliche Anweisungen (nur für diese Generierung)
                  </button>
                  {showGuidanceThema && (
                    <div className="mt-3">
                      <Textarea
                        minRows={5}
                        maxRows={20}
                        value={userGuidanceThema}
                        onChange={(e) => setUserGuidanceThema(e.target.value)}
                        placeholder={
                          'z.B. "Fokus auf Diskussion der Ausschussmitglieder", ' +
                          '"kompakter halten", "keine Zwischenüberschriften", ' +
                          '"jedes Thema in maximal 3 Sätzen".'
                        }
                        className="w-full"
                      />
                      <div className="mt-1 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setUserGuidanceThema("")}
                          className="text-xs text-onSurface/60 hover:text-onSurface underline disabled:opacity-40"
                          disabled={!userGuidanceThema}
                        >
                          Zurücksetzen
                        </button>
                        {userGuidanceThema.trim() && (
                          <span className="text-xs text-onSurface/50">
                            Wird als vorrangige Anweisung an die KI übergeben (Ton, Umfang, Struktur-Präferenzen). Nur Ausgabeformat und Konjunktiv-/Präteritum-Regel bleiben bindend.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 flex-wrap">
                  <AppButton
                    width="w-[220px]"
                    className="px-4"
                    disabled={isSummarizingThema}
                    onClick={() => {
                      if (!summaryName.trim()) {
                        addToast({ title: "Bitte einen Titel eingeben", color: "danger" });
                        return;
                      }
                      handleSummarizeSections();
                    }}
                  >
                    {isSummarizingThema ? (
                      <><svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Wird erstellt...</>
                    ) : "Thema zusammenfassen"}
                  </AppButton>
                </div>
              </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-white rounded-lg shadow-sm">
                <div className="text-center text-onSurface/60">
                  <p className="text-lg">Noch keine Sektionen</p>
                  <p className="text-sm mt-2 opacity-70">Klicken Sie auf die Schaltfläche \"Hinzufügen\", um Ihre erste Sektion zu erstellen</p>
                </div>
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-onSurface p-2 mb-4">Artikel</div>
              <div className="flex flex-col bg-white rounded-lg p-6 shadow-sm overflow-hidden">
                <div className="mb-4">
                  <Input
                    label="Zusammenfassungstitel"
                    placeholder="z.B. Vorstand Q1"
                    value={articleSummaryName}
                    onChange={(e) => setArticleSummaryName(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="text-sm text-onSurface/70 leading-relaxed">
                  Erstellt eine zusammengeführte Artikel-Zusammenfassung aus den ausgewählten Absätzen
                  über mehrere Sitzungen.<br />Die Ausgabe wird als eine einzige Zusammenfassung generiert.
                </div>

                {/* Free-text user guidance — prepended to the drafter system
                    prompt with top priority (see with_user_priority in
                    prompts.py). Empty = no guidance sent. */}
                <div className="mt-4 border-t border-gray-200 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowGuidanceArtikel((v) => !v)}
                    className="text-xs text-onSurface/70 hover:text-onSurface flex items-center gap-1"
                  >
                    {showGuidanceArtikel ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Zusätzliche Anweisungen (nur für diese Generierung)
                  </button>
                  {showGuidanceArtikel && (
                    <div className="mt-3">
                      <Textarea
                        minRows={5}
                        maxRows={20}
                        value={userGuidanceArtikel}
                        onChange={(e) => setUserGuidanceArtikel(e.target.value)}
                        placeholder={
                          'z.B. "Nur Artikel 1 bis 5 zusammenfassen", ' +
                          '"pro Artikel maximal 5 Sätze", ' +
                          '"Fokus auf Änderungsanträge und Abstimmungen".'
                        }
                        className="w-full"
                      />
                      <div className="mt-1 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setUserGuidanceArtikel("")}
                          className="text-xs text-onSurface/60 hover:text-onSurface underline disabled:opacity-40"
                          disabled={!userGuidanceArtikel}
                        >
                          Zurücksetzen
                        </button>
                        {userGuidanceArtikel.trim() && (
                          <span className="text-xs text-onSurface/50">
                            Wird als vorrangige Anweisung an die KI übergeben (Ton, Umfang, Struktur-Präferenzen). Nur Ausgabeformat und Konjunktiv-Regel bleiben bindend.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 flex-wrap">
                  <AppButton
                    width="w-[220px]"
                    className="px-4"
                    disabled={isSummarizingArtikel}
                    onClick={() => {
                      if (!articleSummaryName.trim()) {
                        addToast({ title: "Bitte einen Titel eingeben", color: "danger" });
                        return;
                      }
                      handleSummarizeArticles();
                    }}
                  >
                    {isSummarizingArtikel ? (
                      <><svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Wird erstellt...</>
                    ) : "Artikel zusammenfassen"}
                  </AppButton>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Accordions View */
          <div className="flex-1 flex flex-col min-h-0 h-full rounded-xl">
          <div className="flex-1 min-h-0 overflow-hidden p-6 rounded-xl">
          {selectedSession ? (
            isSelectedSessionLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-onSurface/60">
                  <div className="inline-flex items-center gap-2 rounded-full bg-surface px-4 py-2">
                    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                    <span className="text-sm">Lade Sitzung…</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
            {(() => {
                  return selectedParagraphsWithSpeakers.length > 0 ? (
                    <div className="flex flex-col h-full">
                      <div className="flex justify-end pb-3">
                        <Button
                          size="sm"
                          variant="light"
                          className="text-danger"
                          onPress={() => {
                            if (!selectedSession?.sessionId) return;
                            const sessionIdStr = String(selectedSession.sessionId);
                            ensureSelectedItemsStoreForCurrentUser();
                            window.selectedItemsStore.set(sessionIdStr, []);
                            try {
                              const parsed = loadSelectedItemsForCurrentUser();
                              parsed[sessionIdStr] = [];
                              saveSelectedItemsForCurrentUser(parsed);
                            } catch (e) {
                              console.warn("[SitzungViewConfig] Failed to persist selectedItemsStore:", e);
                            }
                            window.dispatchEvent(new CustomEvent("selected-items-store-updated"));
                          }}
                        >
                          Alle löschen
                        </Button>
                      </div>
                      <div className="gap-3 overflow-y-auto h-full pr-2">
                        {selectedParagraphsWithSpeakers.map(({ paragraph: para, speaker, speakerKey, speakerLabel }, index) => {
                          // Get time from paragraph
                          const timeDisplay = para.start != null && !isNaN(para.start)
                            ? `${Math.floor(para.start / 60)}:${String(Math.floor(para.start % 60)).padStart(2, '0')}`
                            : "0:00";
                          
                          const timeRange = para.end != null && !isNaN(para.end)
                            ? ` - ${Math.floor(para.end / 60)}:${String(Math.floor(para.end % 60)).padStart(2, '0')}`
                            : '';
                          
                          return (
                            <CustomAccordionItem
                              key={`${para.id}-${index}`}
                              para={para}
                              speakerLabel={speakerLabel}
                              timeDisplay={timeDisplay}
                              timeRange={timeRange}
                              selectedSession={selectedSession}
                              onRemove={removeSelectedParagraph}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center text-onSurface/60">
                        <p className="text-lg">Keine Elemente für \"{selectedSession.label}\" ausgewählt</p>
                        <p className="text-sm mt-2 opacity-70">Wählen Sie Sprecher-Absätze im Navigationsbereich aus, um sie hier anzuzeigen</p>
                      </div>
                    </div>
                  );
                })()}
              </>
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-onSurface/60">
                <p className="text-lg">Wählen Sie eine Sitzung aus dem linken Bereich aus</p>
                <p className="text-sm mt-2 opacity-70">um ihre Sprecher anzuzeigen und zu verwalten</p>
              </div>
            </div>
          )}
        </div>
        </div>
        )}
        </div>
      </div>
    </div>
      <Modal isOpen={isResetOpen} onOpenChange={onResetChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Alle Auswahlen löschen?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-onSurface/70">
                  Dies entfernt alle ausgewählten Sprecher-Absätze aus allen Sitzungen. Dieser Vorgang kann
                  nicht rückgängig gemacht werden.
                </p>
              </ModalBody>
              <ModalFooter className="flex gap-3">
                <Button variant="bordered" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={() => {
                    onClose();
                    handleResetAllSelections();
                  }}
                >
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isDeleteSessionOpen} onOpenChange={onDeleteSessionChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Auswahl dieser Sitzung löschen?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-onSurface/70">
                  Die ausgewählten Sprecher-Absätze für
                  {" "}
                  <strong>{pendingDeleteSession?.label || "diese Sitzung"}</strong>
                  {" "}
                  werden entfernt. Dieser Vorgang kann nicht rückgängig gemacht werden.
                </p>
              </ModalBody>
              <ModalFooter className="flex gap-3">
                <Button
                  variant="bordered"
                  radius="full"
                  onPress={() => {
                    setPendingDeleteSession(null);
                    onClose();
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={() => {
                    if (pendingDeleteSession?.sessionId) {
                      clearSessionSelections(pendingDeleteSession.sessionId);
                    }
                    setPendingDeleteSession(null);
                    onClose();
                  }}
                >
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default SitzungViewConfig;
